// useTodayDiary — fetches today's bookings (across all drivers) for the
// /diary/today page.
//
// /api/v1/collections/diary requires (driver_id, week_start) — it is per-driver-per-week
// and not usable for "today across every driver". So we fetch from
// /api/v1/collections/bookings filtered to today's date and group client-side.
//
// Refresh: refetchInterval 60s, staleTime 55s.

import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../../api/client';
import { endpoints } from '../../../api/endpoints';
import { queryKeys } from '../../../api/queryKeys';
import { useAuthStore } from '../../../store/authStore';
import type { BookingListResponse, BookingSummary } from '../../../types/api';

const STALE_MS = 55_000;
const REFETCH_MS = 60_000;
const PAGE_LIMIT = 200;

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

export function todayISO(now: Date = new Date()): string {
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

export interface UseTodayDiaryOptions {
  /** Override "today" — used by tests. */
  date?: string;
  /** Optional driver id filter; null = all drivers. */
  driverId?: number | null;
}

export function useTodayDiary(options: UseTodayDiaryOptions = {}) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const date = options.date ?? todayISO();
  const driverId = options.driverId ?? null;

  return useQuery<BookingSummary[]>({
    queryKey: queryKeys.diary.today(date, driverId),
    queryFn: async () => {
      const params: Record<string, string | number> = {
        date_from: date,
        date_to: date,
        limit: PAGE_LIMIT,
        sort: 'date_asc',
      };
      if (driverId !== null) params.driver = driverId;
      const res = await apiClient.get<BookingListResponse>(endpoints.bookings.list, { params });
      return res.data.bookings;
    },
    enabled: isAuthenticated,
    staleTime: STALE_MS,
    refetchInterval: REFETCH_MS,
  });
}
